(() => {
  const OFFER_LABELS = {
    coffret: 'Écrin complet',
    pendentif: 'Pendentif seul',
    or9: 'Or 9ct'
  };

  const DEFAULT_LOCALE = 'fr-FR';
  const ENGRAVING_MAX_LENGTH = 20;

  function safeParseJSON(node) {
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[ALOIZE Souvenir Animal] Impossible de lire la configuration JSON.', error);
      return null;
    }
  }

  function formatMoney(cents, currency, locale) {
    const normalizedLocale = locale && locale.includes('-') ? locale : DEFAULT_LOCALE;

    try {
      return new Intl.NumberFormat(normalizedLocale, {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format((cents || 0) / 100);
    } catch (error) {
      return `${Math.round((cents || 0) / 100)} €`;
    }
  }

  function scrollToElement(element) {
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - 90;

    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  }

  function isAcceptedImageFile(file) {
    if (!file) return false;

    if (file.type && file.type.startsWith('image/')) {
      return true;
    }

    const fileName = file.name ? file.name.toLowerCase() : '';

    return (
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.webp') ||
      fileName.endsWith('.heic') ||
      fileName.endsWith('.heif')
    );
  }

  function initSouvenirAnimal(root) {
    const configNode = root.querySelector('[data-asr-config]');
    const config = configNode ? safeParseJSON(configNode) : null;

    if (!config) return;

    const form = document.getElementById(config.formId);
    const uploadCard = root.querySelector('[id^="asr-upload-"]');
    const fileInput = document.getElementById(config.photoInputId);
    const previewImage = root.querySelector('[data-asr-preview]');
    const fileNameNode = root.querySelector('[data-asr-file-name]');
    const postUpload = root.querySelector('[data-asr-post-upload]');
    const errorNode = root.querySelector('[data-asr-error]');
    const variantInput = root.querySelector('[data-asr-variant-id]');
    const totalNodes = root.querySelectorAll('[data-asr-total], [data-asr-sticky-total]');
    const stickyLabel = root.querySelector('[data-asr-sticky-label]');
    const stickyBar = root.querySelector('[data-asr-sticky]');
    const submitButtons = root.querySelectorAll('[data-asr-submit], [data-asr-sticky-submit]');
    const stickyUploadButton = root.querySelector('[data-asr-sticky-upload]');
    const stickySubmitButton = root.querySelector('[data-asr-sticky-submit]');
    const engravingToggle = root.querySelector('[data-asr-engraving-toggle]');
    const engravingField = root.querySelector('[data-asr-engraving-field]');
    const engravingInput = root.querySelector('[data-asr-engraving-text]');
    const engravingCounter = root.querySelector('[data-asr-engraving-count]');
    const engravingStyleInputs = root.querySelectorAll('[data-asr-engraving-style]');
    const offerProperty = root.querySelector('[data-asr-offer-property]');
    const engravingProperty = root.querySelector('[data-asr-engraving-property]');
    const photoAddedProperty = root.querySelector('[data-asr-photo-added-property]');
    const offerButtons = root.querySelectorAll('[data-asr-offer]');
    const variantWarning = root.querySelector('[data-asr-variant-warning]');

    let selectedOffer = 'coffret';
    let hasPhoto = false;
    let isSubmitting = false;

    function getVariantKey() {
      const suffix = engravingToggle && engravingToggle.checked ? 'avec_gravure' : 'sans_gravure';
      return `${selectedOffer}_${suffix}`;
    }

    function getCurrentVariant() {
      const key = getVariantKey();
      const variant = config.variants ? config.variants[key] : null;

      if (!variant || !variant.id) {
        console.warn(
          `[ALOIZE Souvenir Animal] Mapping variante introuvable ou invalide : ${key}`,
          {
            key,
            selectedOffer,
            engraving: Boolean(engravingToggle && engravingToggle.checked),
            variants: config.variants
          }
        );

        return null;
      }

      return variant;
    }

    function showError(message) {
      if (!errorNode) return;

      if (!message) {
        errorNode.hidden = true;
        errorNode.textContent = '';
        return;
      }

      errorNode.hidden = false;
      errorNode.textContent = message;
    }

    function setSubmitEnabled(enabled) {
      submitButtons.forEach((button) => {
        button.disabled = !enabled || isSubmitting;
      });
    }

    function updateOfferSelectionUI() {
      offerButtons.forEach((button) => {
        const isActive = button.dataset.asrOffer === selectedOffer;

        button.classList.toggle('is-selected', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function updateEngravingCounter() {
      if (!engravingCounter || !engravingInput) return;

      if (engravingInput.value.length > ENGRAVING_MAX_LENGTH) {
        engravingInput.value = engravingInput.value.slice(0, ENGRAVING_MAX_LENGTH);
      }

      engravingCounter.textContent = `${engravingInput.value.length}/${ENGRAVING_MAX_LENGTH}`;
    }

    function updateEngravingUI() {
      const engravingEnabled = Boolean(engravingToggle && engravingToggle.checked);

      if (engravingField) {
        engravingField.hidden = !engravingEnabled;
      }

      if (engravingInput) {
        engravingInput.disabled = !engravingEnabled;
        engravingInput.required = engravingEnabled;
        engravingInput.maxLength = ENGRAVING_MAX_LENGTH;

        if (!engravingEnabled) {
          engravingInput.value = '';
        } else if (engravingInput.value.length > ENGRAVING_MAX_LENGTH) {
          engravingInput.value = engravingInput.value.slice(0, ENGRAVING_MAX_LENGTH);
        }
      }

      engravingStyleInputs.forEach((input) => {
        input.disabled = !engravingEnabled;
      });

      if (engravingEnabled) {
        const hasCheckedStyle = Array.from(engravingStyleInputs).some((input) => input.checked);

        if (!hasCheckedStyle && engravingStyleInputs[0]) {
          engravingStyleInputs[0].checked = true;
        }
      }

      if (engravingProperty) {
        engravingProperty.value = engravingEnabled ? 'Oui' : 'Non';
      }

      updateEngravingCounter();
    }

    function updateVariantAndTotal() {
      const variant = getCurrentVariant();

      if (variantInput) {
        variantInput.value = variant ? variant.id : '';
      }

      if (variantWarning) {
        if (!variant) {
          variantWarning.hidden = false;
          variantWarning.textContent = 'Variante introuvable : vérifier les noms exacts des options Shopify.';
        } else {
          variantWarning.hidden = true;
          variantWarning.textContent = '';
        }
      }

      const price = variant ? variant.price : 0;
      const formattedPrice = formatMoney(price, config.currency, config.locale);

      totalNodes.forEach((node) => {
        node.textContent = formattedPrice;
      });

      if (stickyLabel) {
        stickyLabel.textContent = OFFER_LABELS[selectedOffer] || 'Bijou souvenir';
      }

      if (offerProperty) {
        offerProperty.value = OFFER_LABELS[selectedOffer] || selectedOffer;
      }

      return variant;
    }

    function updateSubmitLabels() {
      submitButtons.forEach((button) => {
        if (!isSubmitting) {
          button.textContent = 'Valider mon bijou souvenir';
        }
      });
    }

    function updateStickyState() {
      if (!stickyUploadButton || !stickySubmitButton) return;

      if (hasPhoto) {
        stickyUploadButton.hidden = true;
        stickySubmitButton.hidden = false;
      } else {
        stickyUploadButton.hidden = false;
        stickySubmitButton.hidden = true;
      }
    }

    function updateStickyVisibility() {
      if (!stickyBar) return;

      const shouldShow = window.scrollY > 220;
      stickyBar.classList.toggle('is-visible', shouldShow);
    }

    function updateState() {
      updateOfferSelectionUI();
      updateEngravingUI();

      const variant = updateVariantAndTotal();

      updateSubmitLabels();
      updateStickyState();
      updateStickyVisibility();

      const engravingEnabled = Boolean(engravingToggle && engravingToggle.checked);
      const engravingText = engravingInput ? engravingInput.value.trim() : '';
      const engravingValid = !engravingEnabled || (engravingText.length > 0 && engravingText.length <= ENGRAVING_MAX_LENGTH);

      setSubmitEnabled(Boolean(hasPhoto && variant && engravingValid));
    }

    function handleFileChange() {
      showError('');

      const file = fileInput && fileInput.files ? fileInput.files[0] : null;

      if (!file) {
        hasPhoto = false;

        if (postUpload) postUpload.hidden = true;

        if (previewImage) {
          previewImage.hidden = true;
          previewImage.removeAttribute('src');
        }

        if (fileNameNode) fileNameNode.textContent = '';
        if (photoAddedProperty) photoAddedProperty.value = 'Non';

        updateState();
        return;
      }

      if (!isAcceptedImageFile(file)) {
        hasPhoto = false;
        fileInput.value = '';
        showError('Merci d’ajouter une image au format JPG, PNG, WEBP ou HEIC.');
        updateState();
        return;
      }

      hasPhoto = true;

      if (photoAddedProperty) {
        photoAddedProperty.value = 'Oui';
      }

      if (fileNameNode) {
        fileNameNode.textContent = file.name;
      }

      if (postUpload) {
        postUpload.hidden = false;
      }

      if (previewImage) {
        const reader = new FileReader();

        reader.onload = (event) => {
          previewImage.src = event.target.result;
          previewImage.hidden = false;
        };

        reader.onerror = () => {
          previewImage.hidden = true;
          console.warn('[ALOIZE Souvenir Animal] Miniature impossible à générer pour ce fichier.');
        };

        reader.readAsDataURL(file);
      }

      if (uploadCard) {
        scrollToElement(uploadCard);
      }

      updateState();
    }

    function handleOfferClick(event) {
      const button = event.currentTarget;
      const offer = button.dataset.asrOffer;

      if (!offer) return;

      selectedOffer = offer;
      updateState();
    }

    function validateBeforeSubmit(event) {
      showError('');

      const variant = getCurrentVariant();

      if (!variant) {
        event.preventDefault();
        showError('La variante Shopify est introuvable. Vérifier les options du produit.');
        updateState();
        return;
      }

      const file = fileInput && fileInput.files ? fileInput.files[0] : null;

      if (!file) {
        event.preventDefault();
        hasPhoto = false;
        showError('Ajoutez d’abord la photo à graver.');
        updateState();

        if (uploadCard) {
          scrollToElement(uploadCard);
        }

        return;
      }

      if (engravingToggle && engravingToggle.checked) {
        const engravingText = engravingInput ? engravingInput.value.trim() : '';

        if (!engravingText) {
          event.preventDefault();
          showError('Indiquez le texte à graver au dos, ou décochez l’option gravure.');
          if (engravingInput) engravingInput.focus();
          updateState();
          return;
        }

        if (engravingText.length > ENGRAVING_MAX_LENGTH) {
          event.preventDefault();
          showError(`Le texte de gravure doit contenir ${ENGRAVING_MAX_LENGTH} caractères maximum.`);
          if (engravingInput) engravingInput.focus();
          updateState();
          return;
        }
      }

      isSubmitting = true;

      submitButtons.forEach((button) => {
        button.disabled = true;
        button.textContent = 'Ajout au panier…';
      });
    }

    offerButtons.forEach((button) => {
      button.addEventListener('click', handleOfferClick);
    });

    if (fileInput) {
      fileInput.addEventListener('change', handleFileChange);
    } else {
      console.warn('[ALOIZE Souvenir Animal] Champ fichier introuvable.', {
        photoInputId: config.photoInputId
      });
    }

    if (engravingToggle) {
      engravingToggle.addEventListener('change', updateState);
    }

    if (engravingInput) {
      engravingInput.addEventListener('input', updateState);
    }

    engravingStyleInputs.forEach((input) => {
      input.addEventListener('change', updateState);
    });

    if (form) {
      form.addEventListener('submit', validateBeforeSubmit);
    } else {
      console.warn('[ALOIZE Souvenir Animal] Formulaire produit introuvable.', {
        formId: config.formId
      });
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });

    Object.entries(config.variants || {}).forEach(([key, variant]) => {
      if (!variant || !variant.id) {
        console.warn(`[ALOIZE Souvenir Animal] Variante non mappée : ${key}`, variant);
      }
    });

    updateState();
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-asr-section]').forEach((root) => {
      try {
        initSouvenirAnimal(root);
      } catch (error) {
        console.warn('[ALOIZE Souvenir Animal] Initialisation interrompue.', error);
      }
    });
  });
})();